"use client";

import { useEffect, useMemo, useState } from "react";
import { useFetchData } from "../../../hooks/useFetch";
import { isSuccess } from "../../../models/response/base-response";
import { ApiConfig, API_ORDER_EMPLOYEES_ROUTER } from "../../../services/api-config";
import { showToast } from "../../../components/custom/custom-toast";
import { downloadOrderServiceExcel } from "../utils/download-excel";

interface BranchStepRow {
  branch_id: number | null;
  branch_name: string;
  // Chỉ 4 vai được tính sản lượng (BE lọc ở `REPORTED_ROLE_TYPES`): nhận · giặt · giao · kiểm đếm.
  pickup_kg: number;
  wash_kg: number;
  delivery_kg: number;
  count_kg: number;
  total_kg: number;
  order_count: number;
}

interface OrderRow {
  order_id: number;
  order_code: string;
  customer_name?: string;
  branch_id: number | null;
  branch_name: string;
  role_type: number;
  role_name: string;
  kg: number;
  item_count: number;
  completed_at: string | null;
}

interface Props {
  employeeId: number;
  employeeName: string;
  branchId?: number | "";
  startDate?: string; // YYYY-MM-DD
  endDate?: string;   // YYYY-MM-DD
  onClose: () => void;
}

const ORDER_PAGE_SIZE = 20;

// Làm tròn tối đa 1 chữ số thập phân, bỏ ".0" thừa: 23→"23", 11.53→"11.5". Ô bảng: 0 hiện "-".
const formatKg = (v: number) => (Number(v) > 0 ? String(Math.round((Number(v) || 0) * 10) / 10) : "-");

const formatDateTime = (v?: string | null) => {
  if (!v) return "—";
  // Server trả chuỗi ISO/UTC; hiện dd/MM/yyyy HH:mm
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const EmployeeKgDetailModal: React.FC<Props> = ({ employeeId, employeeName, branchId, startDate, endDate, onClose }) => {
  const [byBranch, setByBranch] = useState<BranchStepRow[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  // Danh sách đơn phân trang ở BE (ORDER_PAGE_SIZE dòng/trang); `orderTotal` = tổng số dòng để tính số trang.
  const [orderTotal, setOrderTotal] = useState(0);
  const [orderPage, setOrderPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [exporting, setExporting] = useState(false);

  const { request } = useFetchData.Get<any>({ projectId: ApiConfig.PROJECT_ID.ORDER_SERVICE });

  useEffect(() => { setOrderPage(1); }, [employeeId, branchId, startDate, endDate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoading(true);
      try {
        const data: any = { employee_id: employeeId, page: orderPage, limit: ORDER_PAGE_SIZE };
        if (branchId) data.branch_id = branchId;
        if (startDate) data.start_date = startDate;
        if (endDate) data.end_date = endDate;
        const res = await request({ endPoint: API_ORDER_EMPLOYEES_ROUTER.API_KG_REPORT_DETAIL, data });
        if (mounted && res && isSuccess(res)) {
          setByBranch(res.data?.by_branch ?? []);
          setOrders(res.data?.orders ?? []);
          setOrderTotal(Number(res.data?.order_total ?? res.data?.orders?.length ?? 0));
        }
      } catch (e) {
        console.error("Failed to load kg detail:", e);
      } finally {
        if (mounted) {
          setIsLoading(false);
          setLoaded(true);
        }
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId, branchId, startDate, endDate, orderPage]);

  const orderPages = Math.max(1, Math.ceil(orderTotal / ORDER_PAGE_SIZE));

  const totals = useMemo(() => {
    const t = byBranch.reduce(
      (a, r) => ({
        pickup_kg: a.pickup_kg + r.pickup_kg,
        wash_kg: a.wash_kg + r.wash_kg,
        delivery_kg: a.delivery_kg + r.delivery_kg,
        count_kg: a.count_kg + r.count_kg,
        total_kg: a.total_kg + r.total_kg,
        order_count: a.order_count + r.order_count,
      }),
      { pickup_kg: 0, wash_kg: 0, delivery_kg: 0, count_kg: 0, total_kg: 0, order_count: 0 }
    );
    return t;
  }, [byBranch]);

  const exportExcel = async () => {
    setExporting(true);
    try {
      const params: Record<string, unknown> = { employee_id: employeeId, start_date: startDate, end_date: endDate };
      if (branchId) params.branch_id = branchId;
      await downloadOrderServiceExcel(
        API_ORDER_EMPLOYEES_ROUTER.API_KG_REPORT_DETAIL_EXPORT,
        params,
        `san-luong_${employeeId}_${startDate}_${endDate}.xlsx`,
      );
    } catch (e: any) {
      showToast({ message: e?.message || "Xuất Excel thất bại", type: "error" });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Chi tiết sản lượng — {employeeName}</h3>
            <p className="text-xs text-gray-400 mt-0.5">Tổng hợp theo chi nhánh thực hiện & danh sách đơn</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={exportExcel}
              disabled={exporting || !loaded || !startDate || !endDate}
              className="flex items-center gap-1.5 h-9 px-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              {exporting ? "Đang xuất..." : "Xuất Excel"}
            </button>
            <button onClick={onClose} className="w-9 h-9 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-6 py-4 space-y-6">
          {/* Chỉ che cả modal ở lần tải đầu; chuyển trang thì giữ nội dung, làm mờ bảng đơn. */}
          {!loaded ? (
            <div className="py-12 text-center text-gray-400 text-sm">Đang tải...</div>
          ) : (
            <>
              {/* (1) Tổng hợp theo chi nhánh × công đoạn */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Theo chi nhánh thực hiện</h4>
                <div className="overflow-x-auto border border-gray-100 rounded-xl">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100 text-gray-600">
                        <th className="px-3 py-2 text-left font-semibold min-w-[140px]">Chi nhánh</th>
                        <th className="px-3 py-2 text-right font-semibold">Nhận hàng</th>
                        <th className="px-3 py-2 text-right font-semibold">Giặt</th>
                        <th className="px-3 py-2 text-right font-semibold">Giao</th>
                        <th className="px-3 py-2 text-right font-semibold">Kiểm đếm</th>
                        <th className="px-3 py-2 text-right font-semibold text-blue-600 bg-blue-50/50">Tổng (kg)</th>
                        <th className="px-3 py-2 text-center font-semibold">Đơn</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {byBranch.length === 0 ? (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">Không có dữ liệu</td></tr>
                      ) : (
                        byBranch.map((r) => (
                          <tr key={r.branch_id ?? 0} className="hover:bg-gray-50/50">
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-700"> {r.branch_name}</span>
                            </td>
                            <td className="px-3 py-2 text-right text-gray-600">{formatKg(r.pickup_kg)}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{formatKg(r.wash_kg)}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{formatKg(r.delivery_kg)}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{formatKg(r.count_kg)}</td>
                            <td className="px-3 py-2 text-right font-bold text-blue-700 bg-blue-50/30">{formatKg(r.total_kg)}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{r.order_count}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    {byBranch.length > 0 && (
                      <tfoot>
                        <tr className="bg-blue-50/60 border-t-2 border-blue-200 font-bold text-blue-800">
                          <td className="px-3 py-2 text-right text-xs uppercase">Tổng cộng</td>
                          <td className="px-3 py-2 text-right">{formatKg(totals.pickup_kg)}</td>
                          <td className="px-3 py-2 text-right">{formatKg(totals.wash_kg)}</td>
                          <td className="px-3 py-2 text-right">{formatKg(totals.delivery_kg)}</td>
                          <td className="px-3 py-2 text-right">{formatKg(totals.count_kg)}</td>
                          <td className="px-3 py-2 text-right bg-blue-100/50">{formatKg(totals.total_kg)}</td>
                          <td className="px-3 py-2 text-center">{totals.order_count}</td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>

              {/* (2) Danh sách đơn */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Danh sách đơn ({orderTotal})</h4>
                <div className={`overflow-x-auto border border-gray-100 rounded-xl transition-opacity ${isLoading ? "opacity-50" : ""}`}>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100 text-gray-600">
                        <th className="px-3 py-2 text-center font-semibold w-12">#</th>
                        <th className="px-3 py-2 text-left font-semibold">Mã đơn</th>
                        <th className="px-3 py-2 text-left font-semibold">Khách hàng</th>
                        <th className="px-3 py-2 text-left font-semibold">Chi nhánh thực hiện</th>
                        <th className="px-3 py-2 text-left font-semibold">Công đoạn</th>
                        <th className="px-3 py-2 text-right font-semibold">Kg</th>
                        <th className="px-3 py-2 text-center font-semibold">Ngày hoàn thành</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {orders.length === 0 ? (
                        <tr><td colSpan={7} className="px-3 py-6 text-center text-gray-400">Không có đơn</td></tr>
                      ) : (
                        orders.map((o, i) => (
                          <tr key={`${o.order_id}-${o.role_type}-${i}`} className="hover:bg-gray-50/50">
                            <td className="px-3 py-2 text-center text-gray-400">{(orderPage - 1) * ORDER_PAGE_SIZE + i + 1}</td>
                            <td className="px-3 py-2 font-medium text-gray-800">{o.order_code}</td>
                            <td className="px-3 py-2 text-gray-700">{o.customer_name || <span className="text-gray-300">—</span>}</td>
                            <td className="px-3 py-2 text-gray-600">{o.branch_name}</td>
                            <td className="px-3 py-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">{o.role_name}</span>
                            </td>
                            <td className="px-3 py-2 text-right font-semibold text-gray-700">{formatKg(o.kg)}</td>
                            <td className="px-3 py-2 text-center text-gray-500 text-xs">{formatDateTime(o.completed_at)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {orderTotal > ORDER_PAGE_SIZE && (
                  <div className="mt-3 flex items-center justify-between text-sm">
                    <span className="text-gray-500">
                      Hiển thị {(orderPage - 1) * ORDER_PAGE_SIZE + 1}-{Math.min(orderPage * ORDER_PAGE_SIZE, orderTotal)} / {orderTotal}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setOrderPage((p) => Math.max(1, p - 1))}
                        disabled={orderPage === 1 || isLoading}
                        className="px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        ‹
                      </button>
                      <span className="px-2 text-gray-600">
                        Trang {orderPage}/{orderPages}
                      </span>
                      <button
                        onClick={() => setOrderPage((p) => Math.min(orderPages, p + 1))}
                        disabled={orderPage === orderPages || isLoading}
                        className="px-2.5 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        ›
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeKgDetailModal;
